'use client';

/**
 * ChipMultiSelect — emoji-chip multi-select.
 *
 * A flat wrap of toggle chips with an optional leading emoji, used for the
 * Category filter across the discovery feeds. Selecting/deselecting toggles
 * membership in the controlled `value` array; the header shows an "N selected"
 * count once any chip is active. Replaces the legacy single-select category
 * dropdown.
 */
import { c as _c } from "react/compiler-runtime";
import { useReducedMotion } from 'motion/react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { cn } from '@/lib/ui/utils';
export interface ChipOption {
  value: string;
  label: string;
  emoji?: string;
}
export interface ChipMultiSelectProps {
  options: ChipOption[];
  /** Controlled selected values. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Render `<count> selected` in the section header slot. */
  renderCount?: (count: number) => string;
  className?: string;
}
export function ChipMultiSelect(t0) {
  const $ = _c(24);
  const {
    options,
    value,
    onChange,
    renderCount,
    className
  } = t0;
  const shouldReduceMotion = useReducedMotion();
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== className || $[1] !== onChange || $[2] !== options || $[3] !== renderCount || $[4] !== shouldReduceMotion || $[5] !== value) {
    const selected = new Set(value);
    const toggle = function toggle(optionValue) {
      const next = new Set(selected);
      if (next.has(optionValue)) {
        next.delete(optionValue);
      } else {
        next.add(optionValue);
      }
      onChange(options.flatMap(o => next.has(o.value) ? [o.value] : []));
    };
    const count = value.length;
    t3 = "chip-multi-select";
    if ($[11] !== className) {
      t4 = cn("flex flex-col gap-1.5", className);
      $[11] = className;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    if ($[13] !== count || $[14] !== renderCount) {
      t5 = count > 0 && renderCount !== undefined && <span data-slot="chip-multi-select-count" className="text-caption text-foreground-secondary">{renderCount(count)}</span>;
      $[13] = count;
      $[14] = renderCount;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    t1 = "flex flex-wrap items-start gap-1.5";
    t2 = options.map(option => {
      const isOn = selected.has(option.value);
      return <AnimatedButton key={option.value} variant="tertiary" size="sm" disableHoverScale={shouldReduceMotion === true} aria-pressed={isOn} onClick={() => toggle(option.value)} className={cn("h-auto rounded-pill border px-3 py-1.5 text-caption font-medium", isOn ? "border-border-brand bg-background-brand font-medium text-foreground-brand" : "border-border-primary bg-background-tertiary text-foreground-secondary hover:text-foreground-primary")}>{option.emoji !== undefined && <span aria-hidden="true" className="mr-1">{option.emoji}</span>}{option.label}</AnimatedButton>;
    });
    $[0] = className;
    $[1] = onChange;
    $[2] = options;
    $[3] = renderCount;
    $[4] = shouldReduceMotion;
    $[5] = value;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
  }
  let t6;
  if ($[16] !== t1 || $[17] !== t2) {
    t6 = <div className={t1}>{t2}</div>;
    $[16] = t1;
    $[17] = t2;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t3 || $[20] !== t4 || $[21] !== t5 || $[22] !== t6) {
    t7 = <div data-slot={t3} className={t4}>{t5}{t6}</div>;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
}